import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Calendar } from 'lucide-react';
import { DailyMetalData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';

interface MetalRecentDaysTableProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  processes: string[];
  title: string;
}

const MetalRecentDaysTable: React.FC<MetalRecentDaysTableProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  processes, 
  title 
}) => {
  // Process configuration
  const processConfig = {
    phoi: { name: 'Phôi', shortName: 'Phôi', color: 'bg-amber-100' },
    han: { name: 'Hàn', shortName: 'Hàn', color: 'bg-red-100' },
    son: { name: 'Sơn', shortName: 'Sơn', color: 'bg-blue-100' },
    kiemTran: { name: 'Kiểm trần', shortName: 'K.Trần', color: 'bg-violet-100' },
    kiemDan: { name: 'Kiểm đan', shortName: 'K.Đan', color: 'bg-emerald-100' },
    thanhPham: { name: 'Thành phẩm', shortName: 'T.Phẩm', color: 'bg-orange-100' },
  };

  // Get recent 3 days (sorted newest first)
  const recentData = useMemo(() => {
    return [...dailyData]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [dailyData]);

  // Calculate statistics for highlighting (Kg values)
  const processKgStats = useMemo(() => {
    const stats: Record<string, { max: number; min: number; maxDate: string; minDate: string }> = {};
    
    processes.forEach(processKey => {
      const values = recentData.map(day => ({
        value: day.processes[processKey as keyof typeof day.processes].weight || 0,
        date: day.date
      })).filter(item => item.value > 0);

      if (values.length > 0) {
        const maxItem = values.reduce((max, item) => item.value > max.value ? item : max);
        const minItem = values.reduce((min, item) => item.value < min.value ? item : min);
        
        stats[processKey] = {
          max: maxItem.value,
          min: minItem.value,
          maxDate: maxItem.date,
          minDate: minItem.date,
        };
      } else {
        stats[processKey] = { max: 0, min: 0, maxDate: '', minDate: '' };
      }
    });

    return stats;
  }, [recentData, processes]);

  // Format numbers
  // Use Vietnamese locale formatting for numbers

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  // Get cell styling for highlighting
  const getCellStyle = (processKey: string, value: number, date: string, isKg: boolean) => {
    if (!isKg || value === 0) return '';
    
    const stats = processKgStats[processKey];
    if (!stats) return '';

    if (value === stats.max && date === stats.maxDate && stats.max > 0) {
      return 'bg-green-600 text-white font-semibold'; // Green background for max
    }
    if (value === stats.min && date === stats.minDate && stats.min > 0) {
      return 'bg-red-600 text-white font-semibold'; // Red background for min
    }
    return '';
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : recentData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* First Row: Process Names (Merged) */}
                <TableRow>
                  <TableHead rowSpan={2} className="text-center border-r w-[100px] align-middle">
                    Ngày
                  </TableHead>
                  {processes.map((processKey) => {
                    const config = processConfig[processKey as keyof typeof processConfig];
                    return (
                      <TableHead 
                        key={processKey}
                        colSpan={3} 
                        className={`text-center border-r font-semibold ${config.color}`}
                      >
                        {config.name}
                      </TableHead>
                    );
                  })}
                </TableRow>
                
                {/* Second Row: Sub-columns */}
                <TableRow>
                  {processes.map((processKey) => (
                    <React.Fragment key={processKey}>
                      <TableHead className="text-center border-r w-[80px]">Công</TableHead>
                      <TableHead className="text-center border-r w-[80px]">Kg</TableHead>
                      <TableHead className="text-center border-r w-[80px]">NS</TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentData.map((day, index) => (
                  <TableRow key={day.date} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <TableCell className="font-medium text-center border-r">
                      {formatDate(day.date)}
                    </TableCell>
                    
                    {processes.map((processKey) => {
                      const processData = day.processes[processKey as keyof typeof day.processes];
                      return (
                        <React.Fragment key={processKey}>
                          <TableCell className="text-center border-r">
                            {formatNumberDisplay(processData.labor || 0, 0)}
                          </TableCell>
                          <TableCell className={`text-center border-r ${getCellStyle(processKey, processData.weight || 0, day.date, true)}`}>
                            {formatNumberDisplay(processData.weight || 0, 2)}
                          </TableCell>
                          <TableCell className="text-center border-r">
                            {formatNumberDisplay(processData.productivity || 0, 2)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Không có dữ liệu 3 ngày gần nhất</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalRecentDaysTable;